/**
 * Shared types for the discussion-package.
 */

// From discussions/api/discussions.py
export type FeedbackType =
    | "questions"
    | "comments"
    | "answers"
    | "replies"
    | "clarifications"
    | "projecthelp"
    | "projectquestions"
    | "projectanswers";
// These are all the possible types a post may be.
export type PostType =
    | "question"
    | "answer"
    | "reply"
    | "comment"
    | "clarification"
    | "scratchpad"
    | "projecthelp"
    | "projecthelpanswer"
    | "projecteval"
    | "projectevalanswer";
// These are the types of posts that can be changed to another type of post.
// Ie: a "question" can be changed to a "comment".
export type ChangeablePostType =
    | "question"
    | "comment"
    | "clarification"
    | "answer";
// Feedback related to projects
export type ProjectFeedbackType =
    | "projectcritiqueanswer"
    | "projectcritique"
    | "projecteval"
    | "projectevalanswer"
    | "projectfeedback"
    | "projecthelpanswer"
    | "projecthelp";
// Feedback types accessible to moderators in the flag queue switcher.
export type LegacyModerationFeedbackType =
    | "question"
    | "answer"
    | "reply"
    | "comment"
    | "project"
    | "projecthelp" // removed
    | "projectevalanswer"
    | "clarification";

// TODO(slim): This should enumerate content kinds like "Article", "Video", etc.
// TODO(briang): It is also notable that these values might have different capitilzation like "Video" or "video"
// TODO(slim): This type can currently be "Topic" or "FlagQueue". We're abusing it to reuse components for topic and
// flag queue discussions.
export type ContentKind = string;

// Each Post is identified by a unique key.
export type PostKey = string;

export type TabsData = {
    key: FeedbackType;
    label: string;
    buttonContents: string;
    tipLabel: string;
    tipContents: string;
    listTabsLabel: string;
    placeholder: string;
    textAreaLabel: string;
};

// How to display post controls.
// - default: Standard view and controls
// - compact: e.g. in the discussions profile widget
// - maximized: fill the size of the container; expand moderator tools inline
export type ViewMode = "default" | "compact" | "maximized";

export type DiscussionContext = {
    expandFeedbackType?: FeedbackType;
    qaExpandKey?: string;
    view?: ViewMode;
    restrictPosting?: boolean;
    notEnoughPoints?: boolean;
    unverifiedEmail?: string | null | undefined;
    meetsAccountAgeRequirement?: boolean;
    showProjectFeedback?: boolean;
    authorKaid?: string | null;
    // viewMode allows the discussion system to be shown in wide or compact
    // spaces.  The default works in most cases, but the wide mode can be
    // used in the moderator panel where the compact view can be used in
    // smaller widgets, like the profile page
    viewMode?: ViewMode;
    // Moderator mode contains a handful of UI tweaks that allows moderators
    // to move more quickly, including hiding deleted posts, allowing 'skip',
    // showing the full timestamp, hide flagging, etc
    moderatorMode?: boolean;
};

// Adapted from discussions/flaggable.py
// Maps flag strings used on the client to server-side strings.
type FlagTypes = {
    inappropriate: "inappropriate";
    lowquality: "lowquality";
    shouldBeQuestion: "changetoquestion";
    shouldBeTipsOrThanks: "changetocomment";
    spam: "spam";

    // Only used by CS programs themselves, not posts on CS programs.
    plagiarism: "plagiarism";
    "vote-soliciting": "vote-soliciting";
    "using-disallowed": "using-disallowed";
};

export type FlagType = keyof FlagTypes;
export type ServerFlagType = FlagTypes[keyof FlagTypes];

export type LowQualityNotice = {
    feedbackCode: string;
    feedbackType: string;
    feedbackChar: string;
    showLowQualityNotice: boolean;
};

export type DiscussionBadge = {
    name?: string;
    description?: string;
    translatedDescription?: string;
    icons: {
        small?: string;
        smallUrl?: string;
    };
};

// TODO(diedra): Make required types actually required.
// TODO(diedra): Clear out any props we're not using.
export type PostCommon = {
    answerCount?: number;
    appearsAsDeleted?: boolean;
    // TODO(slim): Locally, `authorAvatarName` can be null. Investigate whether this is just for dev accounts with no avatars.
    authorAvatarName?: string;
    authorAvatarSrc: string;
    authorKaid: string;
    authorNickname: string;
    // TODO(ned): remove above items when rest calls have all been removed
    author?: {
        nickname: string;
        kaid: string;
        avatar: {
            imageSrc: string;
            name: string;
        };
    };
    badges?: Array<DiscussionBadge>;
    content: string;
    created?: string | Date; // Not in python signature - can remove?
    // TODO(kevinb): update fixtures to output strings for dates
    date: string | Date;
    definitelyNotSpam?: boolean;
    deleted?: boolean;
    downVoted?: boolean;
    expandKey?: string;
    feedbackType?: string;
    // TODO(diedra): Make this `any` type more specific.
    flaggedBy?: Array<any> | null | undefined;
    // Scratchpads send use a different signature for `flaggedBy`
    flaggedByKaid?: Array<any> | null | undefined;
    flaggedByUser?: boolean;
    // TODO(kevinb): Make flags mandatory and stop sending numberOfFlags
    flags?: Array<FlagType> | null | undefined;
    focus?: {
        topicTitle: string;
        kind: string;
        // Only for videos.
        youtubeId?: string;
        translatedTopicTitle: string;
        title: string;
        topicUrl: string;
        description: string;
        relativeUrl: string;
        translatedTitle: string;
        translatedDescription: string;
        // Only for videos.
        translatedYoutubeId?: string;
        id: string;
    };
    focusIsVideo?: boolean | null | undefined; // Not used?
    focusUrl?: string;
    fromVideoAuthor?: boolean;
    hasAnswered?: boolean; // Question feedback only
    hideFromHotlist?: boolean; // Only used in user program posts
    highlightChangeButton?: boolean; // Not used?
    highlightDeleteButton?: boolean; // Should be determined in frontend
    isOld?: boolean;
    isProjectEval?: boolean; // Could infer from type?
    isProjectEvalAnswer?: boolean; // Could infer from type?
    key: PostKey;
    // TODO(diedra): Make this `any` type more specific.
    lastAnswerDate?: any; // Not used?
    lowQualityScore?: number; // Low-quality feedback
    notifyOnAnswer?: boolean;
    num?: number; // Not in python signature - can remove?
    numberOfFlags?: number; // Get from flags
    permalink?: string;
    programID?: string;
    qualityKind?: string;
    // When we only have answers, they have a direct reference to their parent
    // question.  Otherwise, it is just a key as a reference
    question?: PostCommon;
    questionKey?: string;
    relativeUrl?: string; // Only used in user program posts
    replies?: Array<PostKey>; // Not in python signature - can remove?
    replyExpandKeys: Array<string> | null | undefined;
    replyCount?: number; // https://khanacademy.atlassian.net/browse/LP-4298
    restrictPosting?: boolean; // User programs only?
    showAuthorControls?: boolean; // Use current user + authorKaid? (clarifications only)
    showFromAuthor?: boolean; // Not used?
    showModControls?: boolean; // Can get from user profile/isMod
    sumVotesIncremented: number;
    title?: string; // Only used in Project posts; can get from focus?
    thumbnail?: string; // Only used in Project posts
    type: PostType; // Use feedbackType field - type is a reserved word in GQL
    updated?: string; // Only used in Project posts (timestamp of last revision)
    upVoted?: boolean;
    usesSeoUrlSchema?: boolean; // Not used?
    // Only for videos.
    youtubeId?: string; // Maybe can get from focus
    isLocked?: boolean;
    isPinned?: boolean;
};

// The shape of posts we use in the redux store and discussion-post.jsx.
// TODO(kevinb): remove all unnecessary checks for the existence of answers and replies.
export type Post = PostCommon & {
    answers: Array<PostKey>;
    replies: Array<PostKey>;
    repliesCursor?: string | null | undefined;
};

// The shape of posts we get from the server.
// We don't include replies b/c they're only sent in a separate request
// when the user clicks on "Comments" to see the comments on a question
// or answer.
export type ServerPost = PostCommon & {
    answers?: Array<ServerPost> | null | undefined;
};

export type DiscussionsServerResponse = {
    cursor: string | null | undefined;
    feedback: Array<ServerPost>;
    isComplete: boolean;
    qaExpandKey: string;
    sortedByDate: boolean;
    totalCount: number;
    type: string;
};

type Tab = {
    cursor: string;
    isComplete: boolean;
    postKeys: Array<string>;
    totalCount: number;
};

export type TabsByType = Partial<Record<FeedbackType, Tab>>;

export type SortByType = "top-voted" | "recent" | "trending";

export type FeedbackNotification = {
    notifyOnAnswer: boolean;
};

export enum ModerationFeedbackType {
    ANSWER = "ANSWER",
    COMMENT = "COMMENT",
    PROGRAM = "PROGRAM",
    PROJECT_HELP_QUESTION = "PROJECT_HELP_QUESTION",
    QUESTION = "QUESTION",
    REPLY = "REPLY",
}
